<template>
  <div class="quest-evaluation">
    <div class="quest-evaluation__header">
      <h1
        class="quest-evaluation__title"
        v-html="$options.filters.md('You\'ve done it! Great job! :clap:')" />
    </div>

    <div class="quest-evaluation__summary">
      <div class="quest-evaluation__summary-row">
        <div
          class="quest-evaluation__summary-emoji"
          v-html="$options.filters.md(':stopwatch:')" />
        <div class="quest-evaluation__summary-content">
          <div class="quest-evaluation__summary-value">
            {{ formattedFinalDuration }}
          </div>
          <div class="quest-evaluation__summary-description">
            <span v-if="totalHintPenaltySec > 0">
              You've completed this quest in {{ formattedTotalDuration }} and received a {{ formattedTotalHintPenaltyDuration }} hint penalty.
            </span>
            <span v-else>
              You've completed this quest in {{ formattedTotalDuration }} without any hints, bravo!
            </span>
          </div>
        </div>
      </div>

      <div class="quest-evaluation__summary-row">
        <div
          class="quest-evaluation__summary-emoji"
          v-html="$options.filters.md(':trophy:')" />
        <div class="quest-evaluation__summary-content">
          <div class="quest-evaluation__summary-value">
            {{ leaderboardPlace }}{{ $tc('ordinalSuffix', leaderboardPlace) }}
          </div>
          <div class="quest-evaluation__summary-description">
            You've earned the <strong>{{ leaderboardPlace }}{{ $tc('ordinalSuffix', leaderboardPlace) }} place</strong> <a :href="leaderboardPath" target="_blank">on the leaderboard</a>!
          </div>
        </div>
      </div>

      <div class="quest-evaluation__summary-row" v-if="questFinishTimePercentile > 0">
        <div
          class="quest-evaluation__summary-emoji"
          v-html="$options.filters.md(':chart_with_upwards_trend:')" />
        <div class="quest-evaluation__summary-content">
          <div class="quest-evaluation__summary-value">
            {{ questFinishTimePercentile }}%
          </div>
          <div class="quest-evaluation__summary-description">
            Your time is better than {{ questFinishTimePercentile }}% of all {{ totalQuestParticipants }} participants.
          </div>
        </div>
      </div>
    </div>

    <div class="quest-evaluation__chapters">
      <h2 class="quest-evaluation__chapters-title">
        Time per chapter
      </h2>

      <div class="quest-evaluation__chapters-list">
        <div
          v-for="(chapter, index) in chapterParticipants"
          :key="chapter.id"
          class="quest-evaluation__chapter">
          <div class="quest-evaluation__chapter-number">
            <strong>{{ index + 1 }}</strong>
          </div>
          <div class="quest-evaluation__chapter-name">
            {{ chapter.chapter_name }}
          </div>
          <div class="quest-evaluation__chapter-duration">
            {{ formatDuration(chapter.duration_sec) }}
            <span v-if="chapter.hint_penalty_sec > 0">
              + <span class="red-5">{{ formatDuration(chapter.hint_penalty_sec) }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="quest-evaluation__actions flex justify-center">
      <a
        :href="`/quests/${questSlug}/play`"
        class="btn btn--secondary btn--with-icon">
        <LeftArrowIcon class="btn__icon" />
        Back to Quest
      </a>
    </div>
  </div>
</template>

<script>
import { confettiBoom } from "modules/confetti-cannon";
import LeftArrowIcon from "@images/font_awesome/long-arrow-left-light.svg";

export default {
  components: {
    LeftArrowIcon
  },
  data() {
    return {
      questName: window.state.questName,
      questSlug: window.state.questSlug,
      questFinishTimePercentile: window.state.questFinishTimePercentile,
      totalDurationSec: window.state.totalDurationSec,
      totalQuestParticipants: window.state.totalQuestParticipants,
      seenEvaluation: window.state.seenEvaluation,
      chapterParticipants: window.state.chapterParticipants || [],
      leaderboardPlace: window.state.leaderboardPlace,
      leaderboardPath: window.state.leaderboardPath
    }
  },
  computed: {
    totalHintPenaltySec() {
      return this.chapterParticipants.reduce((sum, cp) => sum + cp.hint_penalty_sec, 0);
    },
    formattedFinalDuration() {
      return this.formatDuration(this.totalDurationSec);
    },
    formattedTotalHintPenaltyDuration() {
      return this.formatDuration(this.totalHintPenaltySec);
    },
    formattedTotalDuration() {
      return this.formatDuration(this.totalDurationSec - this.totalHintPenaltySec);
    }
  },
  mounted() {
    if (!this.seenEvaluation) {
      confettiBoom({
        maxCount: 400
      });
    }

    this.markEvaluationAsSeen();
  },
  methods: {
    formatDuration(seconds) {
      if (!seconds || seconds === 0) {
        return;
      }

      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;

      const parts = [];

      if (hours > 0) {
        parts.push(`${hours}h`);
      }
      if (minutes > 0) {
        parts.push(`${minutes}m`);
      }
      if (secs > 0 || parts.length === 0) {
        parts.push(`${secs}s`);
      }

      return parts.join(' ');
    },

    async markEvaluationAsSeen() {
      try {
        await fetch(`/api/quests/participants/mark_evaluation_seen?quest_slug=${this.questSlug}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          }
        });
      } catch (error) {
        console.error('Failed to mark evaluation as seen:', error);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.quest-evaluation {
  max-width: $px720;
  padding: $px32 $px16;
  margin: 0 auto;

  &__header {
    margin-bottom: $px48;

    text-align: center;
  }

  &__title {
    margin-bottom: $px12;

    @include h1;
  }

  &__summary {
    padding: $px32;
    margin-bottom: $px64;

    border-radius: $px12;

    background-color: $grey-10;
  }

  &__summary-row {
    display: flex;
    align-items: center;
    gap: $px24;

    &:not(:last-child) {
      padding-bottom: $px24;
      margin-bottom: $px24;

      border-bottom: $px1 solid $grey-9;
    }
  }

  &__summary-emoji {
    font-size: $px48;
    line-height: 1;
    flex-shrink: 0;
  }

  &__summary-content {
    flex: 1;
  }

  &__summary-value {
    margin-bottom: $px8;

    color: $blue-5;
    font-size: 36px;
    font-weight: bold;
    line-height: 1.2;
  }

  &__summary-description {
    color: $grey-4;
    font-size: 16px;
    line-height: 1.5;

    a {
      position: relative;
      z-index: 1;
      color: $grey-4;
      text-decoration: none;

      &:after {
        display: block;
        position: absolute;
        bottom: $px1;
        left: 0;
        width: 100%;
        height: 6px;
        z-index: -1;

        background-color: $blue-9;

        content: '';
      }
    }
  }

  &__chapters {
    margin-bottom: 40px;
  }

  &__chapters-title {
    @include h2;
  }

  &__chapters-list {
    overflow: hidden;

    border: $px1 solid $grey-9;
    border-radius: $px8;
  }

  &__chapter {
    display: flex;
    align-items: center;
    padding: $px24;

    border-bottom: $px1 solid $grey-9;

    transition: background-color 0.2s;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: $grey-10;
    }
  }

  &__chapter-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: $px48;
    height: $px48;
    flex-shrink: 0;
    margin-right: $px16;

    border-radius: 50%;

    background-color: $grey-10;

    color: $grey-1;

    @include font-regular;
  }

  &__chapter-name {
    flex: 1;

    @include font-regular;
  }

  &__chapter-duration {
    min-width: $px96;

    @include font-regular;
  }

  &__actions {
    margin-top: $px48;

    text-align: center;
  }
}
</style>
